/**
 * Arcade themes: ten selectable visual worlds for the staff leaderboard at
 * arcade.chugpt.apps.chu.com.au, lifted from the "Arcade Ten Ways" design deck.
 *
 * A theme is PRESENTATION ONLY. Every theme renders the same component tree
 * from the same `/api/leaderboard` payload; a theme supplies colours, fonts,
 * ornament flags and rank nomenclature, nothing else. It cannot change what is
 * fetched, aggregated or displayed, and it cannot reach a control: hide-costs
 * is applied server-side in `backend/src/routes/leaderboard.ts` (spend arrives
 * as null), so no palette can put a dollar figure back on screen.
 *
 * WHY tokens rather than ten stylesheets: the deck's ten mockups each carried
 * their own class prefix so the cascade could not collide. In the real app
 * there is exactly one markup tree, so the equivalent isolation is a single
 * token vocabulary that every theme fills in — one theme can never leak a rule
 * into another, and a new theme cannot accidentally restyle a control it does
 * not know about.
 *
 * Fonts come from Google Fonts (see `index.html`). CHU staff browsers may block
 * fonts.googleapis.com, so every stack below ends in a real system fallback and
 * `fsScale`/`fdScale` are tuned against the fallback metrics as well: a blocked
 * webfont degrades the look, it never breaks the layout.
 */

/**
 * The page SKELETON a theme renders in.
 *
 * A layout is NOT a per-theme fork: eight skeletons serve ten themes, because
 * several themes want the same structure and differ only in palette and
 * ornament (Neon Cabinet and Trading Floor are both `board`, one with the
 * ticker slot on). Forking per theme would be ten places to lose a control,
 * which is exactly what the shared-markup rule was protecting against.
 *
 * Every skeleton renders from the same `BoardModel` and can therefore never
 * reach a withheld figure — see `arcade/viewModel.ts`. A theme declares the
 * skeleton that is ACTUALLY IMPLEMENTED for it, so the structural test in
 * `test/arcadeThemes.test.tsx` (which asserts the rendered tree carries the
 * declared layout) stays honest at every landing rather than describing an
 * intention.
 */
export type ArcadeLayoutId =
  | 'board'
  | 'bento'
  | 'ladder'
  | 'lanes'
  | 'raid'
  | 'flaps'
  | 'motd'

export type ArcadeThemeId =
  | 'cabinet'
  | 'bento'
  | 'ladder'
  | 'district'
  | 'grandprix'
  | 'raid'
  | 'wrapped'
  | 'trading'
  | 'terminal'
  | 'splitflap'

/** Rank nomenclature. Theme-coupled COPY, not colour: the Matrix "COPPERTOP"
 *  ladder reads as a bug in a light corporate skin. Five tiers, best first. */
export interface RankTitles {
  /** Rank 1. */
  top: string
  /** Top 10%. */
  elite: string
  /** Top 25%. */
  high: string
  /** Top 50%. */
  mid: string
  /** Everyone else. Never demeaning — see the deck's constraint 03. */
  base: string
}

export interface ArcadeTheme {
  id: ArcadeThemeId
  /** Shown in the theme picker. */
  name: string
  /** One-line concept, shown as the picker's helper text. */
  tagline: string
  /** The page skeleton this theme renders in. Presentation structure only: a
   *  layout selects which renderer draws the shared `BoardModel`, and can no
   *  more reach a withheld figure than a palette can. */
  layout: ArcadeLayoutId

  /* ---- ground and surfaces ---- */
  /** Page ground. May be a gradient (Wrapped) — it is only ever used as a
   *  `background` value, never as a canvas fillStyle. */
  bg: string
  /** Solid ground. Used where a real colour is required (canvas share card,
   *  the rain fade). Always a plain hex, even when `bg` is a gradient. */
  bgSolid: string
  /** Flat panel surface (board rows, team cards, chips). */
  panel: string
  /** Raised panel (podium plinths, award cards, the profile modal). */
  panel2: string
  /** Hover/active surface. */
  hover: string
  /** The board's own translucent surface over the ground. */
  boardBg: string
  /** Modal backdrop. */
  veil: string

  /* ---- ink ---- */
  ink: string
  inkDim: string
  inkFaint: string

  /* ---- lines ---- */
  line: string
  line2: string

  /* ---- accents ---- */
  accent: string
  /** Readable text/icon colour on top of `accent`. */
  onAccent: string
  gold: string
  cyan: string
  hot: string
  violet: string
  /** Podium 2 and 3. */
  silver: string
  bronze: string
  /** The streak flame badge. */
  flame: string

  /** Shows the scrolling exchange ticker above the board.
   *
   *  A SLOT, not a layout. Trading Floor's quote board minus its daily-change
   *  column (a spend-rank delta, and prohibited) is the ordinary table, so it
   *  does not earn a skeleton of its own — the ticker is the only genuinely
   *  new structure in it, and any theme can switch it on. It carries what the
   *  shared payload actually has: topic volumes, team totals and streak
   *  counts. Never a price, never a movement. */
  ticker?: boolean

  /* ---- alpha derivations (kept explicit so no theme depends on color-mix) ---- */
  /** "This is you" row tint. */
  youTint: string
  /** Row hover tint. */
  rowHover: string
  /** Sparkline area fill under the line. */
  sparkFill: string
  /** Avatar backing square. */
  avatarBg: string

  /* ---- typography ---- */
  /** Headline / label face. Complete stack including a system fallback. */
  fontDisplay: string
  /** Body face. Complete stack including a system fallback. */
  fontBody: string
  /** Body size multiplier. VT323 renders tiny per em, proportional faces do
   *  not, so a shared px value cannot serve both. */
  fsScale: number
  /** Display size multiplier, same reasoning in the other direction: Press
   *  Start 2P is legible at 9px where Bricolage Grotesque is not. */
  fdScale: number
  /** Applied to display text (Grand Prix runs italic uppercase). */
  displayStyle: 'normal' | 'italic'
  displayTransform: 'none' | 'uppercase'
  displayTracking: string

  /* ---- ornament ---- */
  radius: string
  radiusSm: string
  /** CRT scanline overlay. */
  scanlines: boolean
  /** Corner vignette. */
  vignette: boolean
  /** Matrix digital rain behind everything. */
  rain: boolean
  /** Neon text/box glow. Off for the flat, printed and daylight worlds. */
  glow: boolean
  /** Panel drop shadow (the light and tactile themes lean on this instead of
   *  glow to separate surfaces). */
  shadow: string

  rankTitles: RankTitles
  /**
   * SERIES colours. Every categorical mark on the board — topic bars, the
   * server charts, the horse race, the bump lines, the donut — reads from
   * here, in fixed slot order, never cycled through a generated hue.
   *
   * THESE ARE NOT THE THEME'S ACCENTS, AND THAT IS DELIBERATE. The neon
   * accents (`accent`, `gold`, `cyan`, `hot`, `violet`) are chrome: borders,
   * headings, the active pill, the hero numbers. They read beautifully there
   * and they fail as data. Run
   * `scripts/validate_palette.js "<hex,...>" --mode dark --surface <panel>`
   * against a theme's own panel colour and the neon sets fail on the
   * lightness band (they sit far above it), on the chroma floor (the sixth
   * slot in most of them is a near-grey), and on colourblind separation.
   */
  chartColors: string[]
  /** Extra rules appended after the tokenised base sheet. Restyles the SAME
   *  markup — it must never hide or add a control. */
  overlay: string
}

/**
 * The two VALIDATED series palettes, one per surface family.
 *
 * Both were checked with the dataviz validator on 31 Aug 2026 against the
 * surface they actually render on, and both clear every gate. Reproduce with:
 *
 *   node scripts/validate_palette.js \
 *     "#199e70,#c98500,#3987e5,#d55181,#9085e9,#e66767" \
 *     --mode dark --surface '#0a1712'
 *   node scripts/validate_palette.js \
 *     "#0d8c5f,#a06a10,#2a78d6,#c4407a,#5f3fb8,#d03b3b" \
 *     --mode light --surface '#ffffff'
 *
 * DARK  : lightness band PASS · chroma floor PASS · worst adjacent CVD 8.4
 *         (protan) · normal-vision 19.7 · contrast all >= 3:1
 * LIGHT : lightness band PASS · chroma floor PASS · worst adjacent CVD 8.4
 *         (deutan) · normal-vision 16.7 · contrast all >= 3:1
 *
 * THE SLOT ORDER IS THE SAFETY MECHANISM, not a preference. The gates are
 * measured on ADJACENT pairs, so re-ordering these arrays can fail a palette
 * that passed. Re-run the validator on any candidate ordering before changing
 * one, and never append a seventh hue: past six slots, fold to "Other" or
 * facet, because a generated seventh is indistinguishable from an existing
 * one under colour-vision deficiency.
 */
export const VALIDATED_SERIES_DARK = ['#199e70', '#c98500', '#3987e5', '#d55181', '#9085e9', '#e66767']
export const VALIDATED_SERIES_LIGHT = ['#0d8c5f', '#a06a10', '#2a78d6', '#c4407a', '#5f3fb8', '#d03b3b']

/**
 * STATUS colours, fixed and never themed.
 *
 * Reserved: no series ever borrows one of these, and a status is never
 * carried by colour alone — every use ships with a label beside it. That rule
 * is what keeps a denial from reading as "series 4" on the tools view.
 */
export const STATUS_COLORS = {
  good: '#0ca30c',
  warning: '#fab219',
  serious: '#ec835a',
  critical: '#d03b3b',
} as const

const MONO_FALLBACK = 'ui-monospace, SFMono-Regular, Menlo, "Courier New", monospace'
const SANS_FALLBACK = 'system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'

/** 01 — NEON CABINET. The board CHU-GPT already runs, unchanged: it is the
 *  default so a viewer who never opens the picker sees exactly what they saw
 *  yesterday. Every literal here is lifted from the pre-theme ARCADE_CSS. */
const cabinet: ArcadeTheme = {
  id: 'cabinet',
  name: 'Neon Cabinet',
  tagline: 'The CRT high-score table. The current board.',
  layout: 'board',
  bg: '#05080a',
  bgSolid: '#05080a',
  panel: '#0a1712',
  panel2: '#0c1d16',
  hover: '#0c1f17',
  boardBg: 'rgba(6,14,10,.72)',
  veil: 'rgba(2,5,4,.78)',
  ink: '#bfefd2',
  inkDim: '#5f8f74',
  inkFaint: '#2f8f63',
  line: '#10402c',
  line2: '#14361f',
  accent: '#00ff66',
  onAccent: '#05080a',
  gold: '#ffb000',
  cyan: '#22e4ff',
  hot: '#ff2e88',
  violet: '#b46bff',
  silver: '#cfe6da',
  bronze: '#d08a4e',
  flame: '#ff9a3c',
  youTint: 'rgba(0,255,102,.06)',
  rowHover: 'rgba(0,255,102,.05)',
  sparkFill: 'rgba(0,255,102,0.12)',
  avatarBg: '#04100a',
  fontDisplay: `"Press Start 2P", ${MONO_FALLBACK}`,
  fontBody: `"VT323", "Courier New", ${MONO_FALLBACK}`,
  fsScale: 1,
  fdScale: 1,
  displayStyle: 'normal',
  displayTransform: 'none',
  displayTracking: '0',
  radius: '0px',
  radiusSm: '0px',
  scanlines: true,
  vignette: true,
  rain: true,
  glow: true,
  shadow: 'none',
  rankTitles: {
    top: 'THE ONE',
    elite: 'SENTINEL HUNTER',
    high: 'OPERATOR',
    mid: 'REDPILL',
    base: 'COPPERTOP',
  },
  // Was ['#ffb000','#00ff66','#22e4ff','#ff2e88','#b46bff','#cfe6da'] until
  // 31 Aug 2026, and it failed three of the five checks against this theme's
  // own panel (#0a1712): three colours sat outside the lightness band,
  // #cfe6da was under the chroma floor and read as grey, and #00ff66 against
  // #ffb000 separated by only 6.8 deutan against a target of 8. The neon set
  // is still the theme -- it just lives on chrome now, where it reads best.
  chartColors: VALIDATED_SERIES_DARK,
  overlay: '',
}

/** 02 — BENTO HQ. Calm, dense, daylight. The one theme that can go on a screen
 *  in reception without looking like a hacker film. */
const bento: ArcadeTheme = {
  id: 'bento',
  name: 'Bento HQ',
  tagline: 'Calm, dense and professional. Reads in daylight.',
  layout: 'bento',
  bg: '#eef4f0',
  bgSolid: '#eef4f0',
  panel: '#ffffff',
  panel2: '#f8fbf9',
  hover: '#eff7f2',
  boardBg: '#ffffff',
  veil: 'rgba(13,26,20,.42)',
  ink: '#0d1a14',
  inkDim: '#7c8981',
  inkFaint: '#4a5a52',
  line: '#dbe7e0',
  line2: '#eef2ef',
  accent: '#0d8c5f',
  onAccent: '#ffffff',
  gold: '#a06a10',
  cyan: '#0f6f95',
  hot: '#b8386c',
  violet: '#5f3fb8',
  silver: '#7f918a',
  bronze: '#9a6633',
  flame: '#c26212',
  youTint: 'rgba(13,140,95,.08)',
  rowHover: 'rgba(13,140,95,.05)',
  sparkFill: 'rgba(13,140,95,0.14)',
  avatarBg: '#e6efe9',
  fontDisplay: `"Bricolage Grotesque", ${SANS_FALLBACK}`,
  fontBody: `"Instrument Sans", ${SANS_FALLBACK}`,
  fsScale: 0.72,
  fdScale: 1.15,
  displayStyle: 'normal',
  displayTransform: 'none',
  displayTracking: '-0.01em',
  radius: '14px',
  radiusSm: '8px',
  scanlines: false,
  vignette: false,
  rain: false,
  glow: false,
  shadow: '0 14px 34px -22px rgba(13,60,40,.45)',
  rankTitles: {
    top: 'TOP OF THE BOARD',
    elite: 'LEADING',
    high: 'ESTABLISHED',
    mid: 'ACTIVE',
    base: 'GETTING STARTED',
  },
  // Was ['#0d8c5f','#0f6f95','#a06a10','#b8386c','#5f3fb8','#4a5a52'] until
  // 31 Aug 2026. Two of those were under the chroma floor (#4a5a52 read as
  // grey) and #0f6f95 against #0d8c5f measured 14.2 normal-vision deltaE --
  // under the 15 floor, which means full-colour readers could not tell the
  // two apart. That is the one gate the method calls non-dismissable, and
  // this is the DEFAULT theme, so it is the one every viewer sees. The
  // replacement keeps Bento's own green and ochre in slots 1 and 2.
  chartColors: VALIDATED_SERIES_LIGHT,
  overlay: `
.arcade .board, .arcade .team, .arcade .acard, .arcade .pcard, .arcade .member, .arcade .modal { box-shadow: var(--a-shadow); }
.arcade .row.head { letter-spacing: .08em; }
.arcade .bdg { border-radius: 999px; }
`,
}

/** 03 — SEASON LADDER. Esports promotion tiers: indigo ground, violet accent,
 *  a mint promotion colour. The bottom is a development league, never a
 *  wooden spoon. */
const ladder: ArcadeTheme = {
  id: 'ladder',
  name: 'Season Ladder',
  tagline: 'Esports divisions. Promotion, never relegation shaming.',
  layout: 'ladder',
  bg: '#0c0e1a',
  bgSolid: '#0c0e1a',
  panel: '#16182c',
  panel2: '#1b1d35',
  hover: '#212343',
  boardBg: 'rgba(22,24,44,.72)',
  veil: 'rgba(6,7,16,.8)',
  ink: '#dfe3ff',
  inkDim: '#7d82ad',
  inkFaint: '#6a6f99',
  line: '#262a45',
  line2: '#1e2138',
  accent: '#8f86ff',
  onAccent: '#0c0e1a',
  gold: '#ffcf5c',
  cyan: '#5ce6a4',
  hot: '#ff6f9c',
  violet: '#b9b3ff',
  silver: '#cfe0ff',
  bronze: '#c9905c',
  flame: '#ffa04a',
  youTint: 'rgba(143,134,255,.12)',
  rowHover: 'rgba(143,134,255,.08)',
  sparkFill: 'rgba(92,230,164,0.16)',
  avatarBg: '#111327',
  fontDisplay: `"Bricolage Grotesque", ${SANS_FALLBACK}`,
  fontBody: `"Instrument Sans", ${SANS_FALLBACK}`,
  fsScale: 0.72,
  fdScale: 1.15,
  displayStyle: 'normal',
  displayTransform: 'none',
  displayTracking: '-0.01em',
  radius: '12px',
  radiusSm: '6px',
  scanlines: false,
  vignette: true,
  rain: false,
  glow: true,
  shadow: '0 18px 40px -26px rgba(0,0,0,.8)',
  rankTitles: {
    top: 'CHAMPION',
    elite: 'GOLD DIVISION',
    high: 'SILVER DIVISION',
    mid: 'BRONZE DIVISION',
    base: 'DEVELOPMENT LEAGUE',
  },
  chartColors: ['#8f86ff', '#5ce6a4', '#ffcf5c', '#ff6f9c', '#cfe0ff', '#b9b3ff'],
  overlay: `
.arcade .plinth { border-top-width: 4px; }
.arcade .team .track, .arcade .lvl .track { border-radius: 999px; }
.arcade .team .fill, .arcade .lvl .fill { border-radius: 999px; }
.arcade .bdg { border-radius: 999px; }
`,
}

/** 04 — DISTRICT MAP. The city-of-teams world reduced to its palette and its
 *  blueprint grid: deep navy ground, cyan survey lines. */
const district: ArcadeTheme = {
  id: 'district',
  name: 'District Map',
  tagline: 'Teams as city districts. Blueprint navy and cyan.',
  layout: 'board',
  bg: '#0a1420',
  bgSolid: '#0a1420',
  panel: '#0c1e2e',
  panel2: '#102a3d',
  hover: '#143247',
  boardBg: 'rgba(12,30,46,.72)',
  veil: 'rgba(4,10,17,.8)',
  ink: '#d6e6f2',
  inkDim: '#5f93b3',
  inkFaint: '#4a7d9c',
  line: '#16324a',
  line2: '#12293c',
  accent: '#22d3ee',
  onAccent: '#06131d',
  gold: '#ffc861',
  cyan: '#6fe3ff',
  hot: '#ff6b9d',
  violet: '#9d8bff',
  silver: '#a9c8dc',
  bronze: '#c08a5a',
  flame: '#ff9a4a',
  youTint: 'rgba(34,211,238,.10)',
  rowHover: 'rgba(34,211,238,.07)',
  sparkFill: 'rgba(34,211,238,0.16)',
  avatarBg: '#07141f',
  fontDisplay: `"Bricolage Grotesque", ${SANS_FALLBACK}`,
  fontBody: `"Instrument Sans", ${SANS_FALLBACK}`,
  fsScale: 0.72,
  fdScale: 1.15,
  displayStyle: 'normal',
  displayTransform: 'none',
  displayTracking: '-0.01em',
  radius: '12px',
  radiusSm: '6px',
  scanlines: false,
  vignette: true,
  rain: false,
  glow: true,
  shadow: '0 18px 40px -26px rgba(0,0,0,.7)',
  rankTitles: {
    top: 'THE CAPITAL',
    elite: 'HIGH DISTRICT',
    high: 'CENTRAL',
    mid: 'BOROUGH',
    base: 'NEW DISTRICT',
  },
  chartColors: ['#22d3ee', '#ffc861', '#9d8bff', '#ff6b9d', '#6fe3ff', '#a9c8dc'],
  overlay: `
/* The isometric survey grid, flattened to a backdrop the board sits on. */
.arcade .board {
  background-image:
    linear-gradient(rgba(30,90,120,.20) 1px, transparent 1px),
    linear-gradient(90deg, rgba(30,90,120,.20) 1px, transparent 1px);
  background-size: 42px 42px;
}
.arcade .team { position: relative; overflow: hidden; }
.arcade .team::after {
  content: ""; position: absolute; inset: auto 0 0 0; height: 2px; background: var(--a-accent); opacity: .35;
}
`,
}

/** 05 — GRAND PRIX. Carbon, racing red, chequered flag. Display type runs
 *  italic uppercase, which is the whole voice of the motorsport world. */
const grandprix: ArcadeTheme = {
  id: 'grandprix',
  name: 'Grand Prix',
  tagline: 'A weekly race to the flag. Carbon and racing red.',
  layout: 'lanes',
  bg: '#141416',
  bgSolid: '#141416',
  panel: '#1a1a1e',
  panel2: '#202024',
  hover: '#26262b',
  boardBg: 'rgba(26,26,30,.86)',
  veil: 'rgba(8,8,10,.82)',
  ink: '#f2efe9',
  inkDim: '#8a8a90',
  inkFaint: '#6a6a70',
  line: '#2a2a2e',
  line2: '#232327',
  accent: '#ff3b30',
  onAccent: '#ffffff',
  gold: '#ffb000',
  cyan: '#4fd2e0',
  hot: '#ff3b30',
  violet: '#b46bff',
  silver: '#d4d4d8',
  bronze: '#c08a4e',
  flame: '#ff7a1a',
  youTint: 'rgba(255,59,48,.10)',
  rowHover: 'rgba(255,59,48,.07)',
  sparkFill: 'rgba(255,176,0,0.16)',
  avatarBg: '#0d0d0e',
  fontDisplay: `"Bricolage Grotesque", ${SANS_FALLBACK}`,
  fontBody: `"Instrument Sans", ${SANS_FALLBACK}`,
  fsScale: 0.72,
  fdScale: 1.15,
  displayStyle: 'italic',
  displayTransform: 'uppercase',
  displayTracking: '-0.005em',
  radius: '10px',
  radiusSm: '5px',
  scanlines: false,
  vignette: true,
  rain: false,
  glow: false,
  shadow: '0 16px 36px -24px rgba(0,0,0,.9)',
  rankTitles: {
    top: 'RACE WINNER',
    elite: 'PODIUM',
    high: 'POINTS FINISH',
    mid: 'MIDFIELD',
    base: 'ON THE GRID',
  },
  chartColors: ['#ff3b30', '#ffb000', '#4fd2e0', '#d4d4d8', '#b46bff', '#8a8a90'],
  overlay: `
/* Chequered flag along the top of the board and each award card. */
.arcade .board, .arcade .acard {
  border-top: 6px solid transparent;
  border-image: repeating-conic-gradient(#f2efe9 0 25%, #141416 0 50%) 6 / 6px / 0 round;
}
.arcade .row .lvl .track, .arcade .team .track { border-radius: 999px; }
.arcade .row .sc { letter-spacing: -0.01em; }
`,
}

/** 06 — RAID BOSS. Co-op, not competition: one shared magenta boss-fight
 *  world where the bars read as health rather than rank. */
const raid: ArcadeTheme = {
  id: 'raid',
  name: 'Raid Boss',
  tagline: 'Co-op, not competition. Everyone pulling the same way.',
  layout: 'raid',
  bg: '#12040e',
  bgSolid: '#12040e',
  panel: '#280618',
  panel2: '#33091f',
  hover: '#3d0c26',
  boardBg: 'rgba(40,6,24,.72)',
  veil: 'rgba(9,2,7,.82)',
  ink: '#ffd9ec',
  inkDim: '#c76a98',
  inkFaint: '#a8547f',
  line: '#45102e',
  line2: '#35102a',
  accent: '#ff2e88',
  onAccent: '#12040e',
  gold: '#ffb000',
  cyan: '#6be3ff',
  hot: '#ff2e88',
  violet: '#c98bff',
  silver: '#f0b8d2',
  bronze: '#d08a5e',
  flame: '#ffb000',
  youTint: 'rgba(255,46,136,.12)',
  rowHover: 'rgba(255,46,136,.08)',
  sparkFill: 'rgba(255,176,0,0.16)',
  avatarBg: '#1c0410',
  fontDisplay: `"Bricolage Grotesque", ${SANS_FALLBACK}`,
  fontBody: `"Instrument Sans", ${SANS_FALLBACK}`,
  fsScale: 0.72,
  fdScale: 1.15,
  displayStyle: 'normal',
  displayTransform: 'none',
  displayTracking: '-0.01em',
  radius: '12px',
  radiusSm: '6px',
  scanlines: false,
  vignette: true,
  rain: false,
  glow: true,
  shadow: '0 18px 44px -26px rgba(0,0,0,.85)',
  rankTitles: {
    top: 'RAID LEADER',
    elite: 'MAIN TANK',
    high: 'VANGUARD',
    mid: 'STRIKE TEAM',
    base: 'PARTY MEMBER',
  },
  chartColors: ['#ff2e88', '#ffb000', '#c98bff', '#6be3ff', '#f0b8d2', '#c76a98'],
  overlay: `
/* Every progress bar reads as boss health: magenta into amber. */
.arcade .team .fill, .arcade .lvl .fill {
  background: linear-gradient(90deg, var(--a-hot), var(--a-gold));
  border-radius: 999px;
}
.arcade .team .track, .arcade .lvl .track { border-radius: 999px; }
.arcade .bdg { border-radius: 999px; }
`,
}

/** 07 — WRAPPED. The personal, shareable story: one vivid gradient, huge type,
 *  glassy cards. Built to be screenshotted, which is exactly what the profile
 *  share card already does. */
const wrapped: ArcadeTheme = {
  id: 'wrapped',
  name: 'Wrapped',
  tagline: 'Your month in AI. Vivid, personal, made to share.',
  layout: 'board',
  bg: 'linear-gradient(160deg,#1b0f3a 0%,#3d1c6e 55%,#c8397f 100%)',
  bgSolid: '#2b1552',
  panel: '#3a1f66',
  panel2: '#472777',
  hover: '#53308a',
  boardBg: 'rgba(255,255,255,.10)',
  veil: 'rgba(20,8,44,.8)',
  ink: '#ffffff',
  inkDim: 'rgba(255,255,255,.72)',
  inkFaint: 'rgba(255,255,255,.58)',
  line: 'rgba(255,255,255,.22)',
  line2: 'rgba(255,255,255,.12)',
  accent: '#ffd654',
  onAccent: '#3d1c6e',
  gold: '#ffd654',
  cyan: '#7fe3ff',
  hot: '#ff8ac4',
  violet: '#c7a6ff',
  silver: '#e6dcff',
  bronze: '#f0a878',
  flame: '#ff8ac4',
  youTint: 'rgba(255,255,255,.16)',
  rowHover: 'rgba(255,255,255,.10)',
  sparkFill: 'rgba(255,214,84,0.22)',
  avatarBg: '#2b1552',
  fontDisplay: `"Bricolage Grotesque", ${SANS_FALLBACK}`,
  fontBody: `"Instrument Sans", ${SANS_FALLBACK}`,
  fsScale: 0.74,
  fdScale: 1.2,
  displayStyle: 'normal',
  displayTransform: 'none',
  displayTracking: '-0.03em',
  radius: '18px',
  radiusSm: '10px',
  scanlines: false,
  vignette: false,
  rain: false,
  glow: false,
  shadow: '0 20px 50px -28px rgba(0,0,0,.6)',
  rankTitles: {
    top: 'YOUR YEAR',
    elite: 'TOP 10%',
    high: 'TOP 25%',
    mid: 'ON THE RISE',
    base: 'JUST GETTING STARTED',
  },
  chartColors: ['#ffd654', '#ff8ac4', '#7fe3ff', '#c7a6ff', '#e6dcff', '#f0a878'],
  overlay: `
/* Glass over the gradient, not opaque panels: the ground is the point. */
.arcade .board, .arcade .team, .arcade .acard, .arcade .pcard, .arcade .member, .arcade .chip {
  background: rgba(255,255,255,.10);
  backdrop-filter: blur(6px);
  box-shadow: var(--a-shadow);
}
.arcade .plinth { background: rgba(255,255,255,.14); backdrop-filter: blur(6px); }
.arcade h1 { letter-spacing: -0.03em; }
.arcade .bdg { border-radius: 999px; background: rgba(255,255,255,.14); }
`,
}

/** 08 — TRADING FLOOR. Adoption as an exchange: mono numerals, tight row
 *  banding, gains green. A natural fit for a finance-literate insurer. */
const trading: ArcadeTheme = {
  id: 'trading',
  name: 'Trading Floor',
  tagline: 'The AI exchange. Live topic and team tape.',
  layout: 'board',
  ticker: true,
  bg: '#080a09',
  bgSolid: '#080a09',
  panel: '#0a120e',
  panel2: '#0d1712',
  hover: '#102019',
  boardBg: 'rgba(10,18,14,.9)',
  veil: 'rgba(3,5,4,.84)',
  ink: '#d6f0dd',
  inkDim: '#5f8f74',
  inkFaint: '#4e7a63',
  line: '#14361f',
  line2: '#0f2418',
  accent: '#00e676',
  onAccent: '#041008',
  gold: '#ffc247',
  cyan: '#4fd2e0',
  hot: '#ff5c7a',
  violet: '#a98bff',
  silver: '#b6c9bd',
  bronze: '#c08a5a',
  flame: '#ffc247',
  youTint: 'rgba(0,230,118,.09)',
  rowHover: 'rgba(0,230,118,.06)',
  sparkFill: 'rgba(0,230,118,0.14)',
  avatarBg: '#050a07',
  fontDisplay: `"Bricolage Grotesque", ${SANS_FALLBACK}`,
  fontBody: `"DM Mono", ${MONO_FALLBACK}`,
  fsScale: 0.7,
  fdScale: 1.15,
  displayStyle: 'normal',
  displayTransform: 'none',
  displayTracking: '-0.01em',
  radius: '6px',
  radiusSm: '3px',
  scanlines: false,
  vignette: false,
  rain: false,
  glow: false,
  shadow: '0 14px 32px -24px rgba(0,0,0,.9)',
  rankTitles: {
    top: 'MARKET LEADER',
    elite: 'BLUE CHIP',
    high: 'LARGE CAP',
    mid: 'MID CAP',
    base: 'NEW LISTING',
  },
  chartColors: ['#00e676', '#ffc247', '#4fd2e0', '#ff5c7a', '#a98bff', '#b6c9bd'],
  overlay: `
/* Quote-board banding and hard tabular numerals. */
.arcade .row:nth-child(even) { background: rgba(255,255,255,.018); }
.arcade .row .sc, .arcade .rank, .arcade .team .amt, .arcade .acard .astat b { font-variant-numeric: tabular-nums; }
.arcade .row { padding-top: 9px; padding-bottom: 9px; }
.arcade .board { border-top: 2px solid var(--a-accent); }
`,
}

/** 09 — RETRO TERMINAL. A single-phosphor CRT MOTD. Deliberately flattens the
 *  class palette to green and amber: a real terminal had two colours, and
 *  committing to that is the whole idea. */
const terminal: ArcadeTheme = {
  id: 'terminal',
  name: 'Retro Terminal',
  tagline: 'Green phosphor MOTD. One screen, two colours.',
  layout: 'motd',
  bg: '#020604',
  bgSolid: '#020604',
  panel: '#05130b',
  panel2: '#071a0e',
  hover: '#0a2614',
  boardBg: 'rgba(3,12,7,.82)',
  veil: 'rgba(1,3,2,.86)',
  ink: '#33ff77',
  inkDim: '#1f9e57',
  inkFaint: '#1f7e47',
  line: '#1f7e47',
  line2: '#0d3a21',
  accent: '#33ff77',
  onAccent: '#020604',
  gold: '#ffcf3c',
  cyan: '#9dffc2',
  hot: '#ffcf3c',
  violet: '#9dffc2',
  silver: '#9dffc2',
  bronze: '#1f9e57',
  flame: '#ffcf3c',
  youTint: 'rgba(51,255,119,.10)',
  rowHover: 'rgba(51,255,119,.07)',
  sparkFill: 'rgba(51,255,119,0.14)',
  avatarBg: '#020604',
  fontDisplay: `"VT323", "Courier New", ${MONO_FALLBACK}`,
  fontBody: `"VT323", "Courier New", ${MONO_FALLBACK}`,
  fsScale: 1,
  // VT323 in the display slot needs the extra size Press Start 2P does not:
  // the 9px labels would otherwise be unreadable.
  fdScale: 1.55,
  displayStyle: 'normal',
  displayTransform: 'uppercase',
  displayTracking: '0.06em',
  radius: '0px',
  radiusSm: '0px',
  scanlines: true,
  vignette: true,
  rain: false,
  glow: true,
  shadow: 'none',
  rankTitles: {
    top: 'ROOT',
    elite: 'SUDOER',
    high: 'OPERATOR',
    mid: 'USER',
    base: 'GUEST',
  },
  // Four slots, not six. The sixth and fifth entries used to REPEAT the
  // first and second (#33ff77 and #ffcf3c twice over), so on a six-category
  // chart two different entities were drawn in one colour -- the same defect
  // as a generated seventh hue and just as invisible. A phosphor terminal
  // genuinely has four distinct data colours; past them `slotColor` returns
  // the neutral, which is the truthful answer rather than a wrap-around.
  chartColors: ['#33ff77', '#ffcf3c', '#9dffc2', '#1f9e57'],
  overlay: `
/* Everything prints; nothing is a card. */
.arcade .row, .arcade .team, .arcade .member { border-style: dashed; }
.arcade .row.head::before { content: "$ "; color: var(--a-ink-dim); }
.arcade .in, .arcade .wrap { text-shadow: 0 0 2px rgba(51,255,119,.5); }
.arcade .bdg { border-style: dashed; }
`,
}

/** 10 — SPLIT-FLAP BOARD. The stadium departures sign: mechanical tiles,
 *  status flags instead of a rank-down arrow. A deliberate counterpoint to
 *  all the neon. */
const splitflap: ArcadeTheme = {
  id: 'splitflap',
  name: 'Split-Flap Board',
  tagline: 'The stadium departures sign. Tactile and analog.',
  layout: 'flaps',
  bg: '#0d0d10',
  bgSolid: '#0d0d10',
  panel: '#141418',
  panel2: '#1a1a1f',
  hover: '#22222a',
  boardBg: 'rgba(20,20,24,.9)',
  veil: 'rgba(5,5,7,.84)',
  ink: '#f4f4ef',
  inkDim: '#8a8a92',
  inkFaint: '#6e6e76',
  line: '#26262c',
  line2: '#1f1f24',
  accent: '#ffb000',
  onAccent: '#0d0d10',
  gold: '#ffb000',
  cyan: '#6fd0e8',
  hot: '#ff7a9c',
  violet: '#b09bff',
  silver: '#c8c8d0',
  bronze: '#c08a5a',
  flame: '#ff9a3c',
  youTint: 'rgba(255,176,0,.10)',
  rowHover: 'rgba(255,176,0,.06)',
  sparkFill: 'rgba(75,224,141,0.16)',
  avatarBg: '#0a0a0d',
  fontDisplay: `"DM Mono", ${MONO_FALLBACK}`,
  fontBody: `"Instrument Sans", ${SANS_FALLBACK}`,
  fsScale: 0.74,
  fdScale: 1.2,
  displayStyle: 'normal',
  displayTransform: 'uppercase',
  displayTracking: '0.08em',
  radius: '8px',
  radiusSm: '4px',
  scanlines: false,
  vignette: true,
  rain: false,
  glow: false,
  shadow: '0 14px 32px -24px rgba(0,0,0,.9)',
  rankTitles: {
    top: 'ON TIME',
    elite: 'DEPARTING',
    high: 'BOARDING',
    mid: 'SCHEDULED',
    base: 'CHECKING IN',
  },
  chartColors: ['#ffb000', '#4be08d', '#6fd0e8', '#ff7a9c', '#b09bff', '#c8c8d0'],
  overlay: `
/* Rank and score render as split-flap tiles: a two-tone card with the
   mechanical hairline across its middle. */
.arcade .rank, .arcade .row .sc, .arcade .plinth .score, .arcade .team .amt {
  position: relative;
  display: inline-block;
  background: linear-gradient(180deg,#26262c 0 49%,#1a1a1f 51% 100%);
  box-shadow: inset 0 0 0 1px #000;
  border-radius: 4px;
  padding: 4px 8px;
}
.arcade .rank::after, .arcade .row .sc::after, .arcade .plinth .score::after, .arcade .team .amt::after {
  content: ""; position: absolute; left: 0; right: 0; top: 50%; height: 1px; background: #000; opacity: .6;
}
.arcade .row .sc { text-align: center; }
.arcade .bdg { border-radius: 4px; }
`,
}

export const ARCADE_THEMES: ArcadeTheme[] = [
  cabinet,
  bento,
  ladder,
  district,
  grandprix,
  raid,
  wrapped,
  trading,
  terminal,
  splitflap,
]

/** The built-in default. A viewer who has never opened the picker, and an
 *  installation whose admin has set no default, sees exactly this.
 *
 *  Bento HQ as of v0.15.0. The arcade is an all-staff link and the calm,
 *  daylight-legible board is the one that reads to someone who did not come
 *  for an arcade; the other nine stay one pick away. A viewer who had already
 *  chosen a cabinet keeps it — the stored choice still wins (useArcadeTheme). */
export const DEFAULT_ARCADE_THEME_ID: ArcadeThemeId = 'bento'

const BY_ID = new Map<string, ArcadeTheme>(ARCADE_THEMES.map((t) => [t.id, t]))

/** Resolves an id (from localStorage, a URL, or the admin default) to a theme,
 *  falling back to the default rather than throwing — an unknown id is a stale
 *  preference, not an error the viewer should see. */
export function resolveArcadeTheme(id: string | null | undefined): ArcadeTheme {
  return (id ? BY_ID.get(id) : undefined) ?? BY_ID.get(DEFAULT_ARCADE_THEME_ID)!
}

export function isArcadeThemeId(id: unknown): id is ArcadeThemeId {
  return typeof id === 'string' && BY_ID.has(id)
}
